import type { ComparisonItem } from '@nfa/types';
import { getProductImageUrl } from '../lib/api';

export interface ComparisonTableProps {
  items: ComparisonItem[];
  onRemove?: (productId: string) => void;
  formatPrice?: (gbp: number) => string;
}

const MAX_ITEMS = 4;

/** Format weight in grams to human-readable string */
function formatWeight(grams: number): string {
  if (grams >= 1000) {
    return `${(grams / 1000).toFixed(1)}kg`;
  }
  return `${grams}g`;
}

/** Format weight difference */
function formatWeightDiff(grams: number): string {
  if (grams >= 1000) {
    return `+${(grams / 1000).toFixed(1)}kg`;
  }
  return `+${grams}g`;
}

/** Format tier label with proper capitalization */
function formatTier(tier: ComparisonItem['tier']): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

/** Format feature key to readable label */
function formatFeatureLabel(key: string): string {
  return key
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Get tier badge color classes */
function getTierClasses(tier: ComparisonItem['tier']): string {
  switch (tier) {
    case 'budget':
      return 'bg-green-500/10 text-green-400';
    case 'premium':
      return 'bg-purple-500/10 text-purple-400';
    default:
      return 'bg-neutral-500/10 text-neutral-400';
  }
}

/** Placeholder for products without images */
function ProductImagePlaceholder() {
  return (
    <div
      data-testid="product-image-placeholder"
      className="w-16 h-16 flex items-center justify-center bg-neutral-800 rounded-lg shrink-0"
    >
      <svg
        className="w-6 h-6 text-neutral-600"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.5}
          d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
        />
      </svg>
    </div>
  );
}

interface ProductImageProps {
  src: string;
  alt: string;
}

/** Product image component */
function ProductImage({ src, alt }: ProductImageProps) {
  return (
    <img
      src={src}
      alt={alt}
      className="w-16 h-16 object-cover rounded-lg shrink-0 bg-neutral-800"
    />
  );
}

/**
 * ComparisonTable - Side-by-side product comparison table
 *
 * Displays products in a table format with visual indicators for best values.
 * Supports up to 4 products for comparison.
 */
export function ComparisonTable({ items, onRemove, formatPrice }: ComparisonTableProps) {
  const defaultFormatPrice = (gbp: number) => `£${gbp}`;
  const priceFormatter = formatPrice ?? defaultFormatPrice;

  // Empty state
  if (items.length === 0) {
    return (
      <div className="text-center py-12 bg-neutral-900 rounded-lg border border-neutral-800">
        <p className="text-neutral-400 text-lg">Add products to compare</p>
      </div>
    );
  }

  // Calculate best values (only meaningful with 2+ items)
  const hasMultipleItems = items.length > 1;
  const prices = items.map((item) => item.price_gbp);
  const minPrice = Math.min(...prices);
  const weights = items.map((item) => item.weight_g).filter((w): w is number => w !== null);
  const minWeight = weights.length > 0 ? Math.min(...weights) : null;

  // Collect all unique feature keys across all items
  const allFeatureKeys = new Set<string>();
  items.forEach((item) => {
    Object.keys(item.features).forEach((key) => allFeatureKeys.add(key));
  });
  const featureKeys = Array.from(allFeatureKeys);

  // Get category from first item (assuming same category for comparison)
  const category = items[0].category_id;

  return (
    <div className="bg-neutral-900 rounded-lg border border-neutral-800 overflow-x-auto">
      {/* Single item suggestion */}
      {items.length === 1 && (
        <div className="px-4 py-2 bg-amber-500/10 border-b border-amber-500/20">
          <p className="text-amber-400 text-sm">Add another product to compare</p>
        </div>
      )}

      {/* Add product button hint (when not at max) */}
      {items.length > 1 && items.length < MAX_ITEMS && (
        <div className="px-4 py-2 bg-neutral-800/50 border-b border-neutral-800">
          <p className="text-neutral-400 text-sm">Add product to compare more items</p>
        </div>
      )}

      {/* Grid-based table structure */}
      <div
        role="table"
        className="grid"
        style={{ gridTemplateColumns: `minmax(120px, 150px) repeat(${items.length}, minmax(180px, 1fr))` }}
      >
        {/* Header row */}
        <div role="row" className="contents">
          {/* Category header */}
          <div
            role="columnheader"
            className="p-4 text-sm font-medium text-neutral-500 uppercase tracking-wider border-b border-neutral-800 bg-neutral-900 sticky left-0"
          >
            <span className="capitalize">{category}</span>
          </div>
          {/* Product name headers */}
          {items.map((item) => {
            // Prefer Cloudflare Images CDN, fall back to external URL proxy
            const imageUrl = getProductImageUrl(item.cf_image_id, item.image_url);
            const hasImage = imageUrl != null;
            const imageAlt = item.image_alt || item.product_name;

            return (
            <div
              key={item.product_id}
              role="columnheader"
              data-testid={`comparison-item-${item.product_id}`}
              className="p-4 border-b border-neutral-800"
            >
              <div className="flex flex-col gap-2">
                {/* Product Image */}
                <div className="mb-2">
                  {hasImage ? (
                    <ProductImage src={imageUrl} alt={imageAlt} />
                  ) : (
                    <ProductImagePlaceholder />
                  )}
                </div>
                <span className="text-white font-semibold text-lg">{item.product_name}</span>
                <span className={`inline-flex w-fit px-2 py-0.5 text-xs rounded ${getTierClasses(item.tier)}`}>
                  {formatTier(item.tier)}
                </span>
                {onRemove && (
                  <button
                    onClick={() => onRemove(item.product_id)}
                    aria-label={`Remove ${item.product_name}`}
                    className="inline-flex w-fit items-center gap-1 px-2 py-1 text-xs text-neutral-400 hover:text-red-400 transition-colors"
                  >
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                    Remove
                  </button>
                )}

                {/* Price cell (nested for within() queries) */}
                <div
                  data-testid="price-cell"
                  className={`mt-2 p-2 rounded ${hasMultipleItems && item.price_gbp === minPrice ? 'bg-green-50' : ''}`}
                >
                  <span className="font-mono font-bold text-lg text-white">
                    {priceFormatter(item.price_gbp)}
                  </span>
                  {hasMultipleItems && (
                    <span className={`block text-xs mt-1 ${item.price_gbp === minPrice ? 'text-green-600 font-medium' : 'text-neutral-500'}`}>
                      {item.price_gbp === minPrice ? 'Lowest' : `+£${item.price_gbp - minPrice}`}
                    </span>
                  )}
                </div>

                {/* Weight cell (nested for within() queries) */}
                <div
                  data-testid="weight-cell"
                  className={`p-2 rounded ${hasMultipleItems && item.weight_g !== null && minWeight !== null && item.weight_g === minWeight ? 'bg-green-50' : ''}`}
                >
                  <span className="font-mono text-white">
                    {item.weight_g !== null ? formatWeight(item.weight_g) : '—'}
                  </span>
                  {hasMultipleItems && item.weight_g !== null && minWeight !== null && (
                    <span className={`block text-xs mt-1 ${item.weight_g === minWeight ? 'text-green-600 font-medium' : 'text-neutral-500'}`}>
                      {item.weight_g === minWeight ? 'Lightest' : formatWeightDiff(item.weight_g - minWeight)}
                    </span>
                  )}
                </div>
              </div>
            </div>
            );
          })}
        </div>

        {/* Price row header (for accessibility) */}
        <div role="row" className="contents">
          <div
            role="rowheader"
            className="p-4 text-sm font-medium text-neutral-400 border-b border-neutral-800 bg-neutral-900 sticky left-0"
          >
            Price
          </div>
          {items.map((item) => (
            <div key={item.product_id} role="cell" className="p-4 border-b border-neutral-800" aria-hidden="true" />
          ))}
        </div>

        {/* Weight row header (for accessibility) */}
        <div role="row" className="contents">
          <div
            role="rowheader"
            className="p-4 text-sm font-medium text-neutral-400 border-b border-neutral-800 bg-neutral-900 sticky left-0"
          >
            Weight
          </div>
          {items.map((item) => (
            <div key={item.product_id} role="cell" className="p-4 border-b border-neutral-800" aria-hidden="true" />
          ))}
        </div>

        {/* Feature rows */}
        {featureKeys.map((featureKey) => (
          <div key={featureKey} role="row" className="contents">
            <div
              role="rowheader"
              className="p-4 text-sm font-medium text-neutral-400 border-b border-neutral-800 bg-neutral-900 sticky left-0"
            >
              {formatFeatureLabel(featureKey)}
            </div>
            {items.map((item) => {
              const value = item.features[featureKey];
              return (
                <div
                  key={item.product_id}
                  role="cell"
                  className="p-4 border-b border-neutral-800 text-neutral-300"
                >
                  {value !== undefined ? String(value) : '—'}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
